"use client";

import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  type FC,
  type Ref,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { mergeClasses, throttle } from "@huub-web/utils";

import { Flex, type FlexProps } from "../Flex";
import { ScrollPane, type ScrollPaneProps } from "../ScrollPane";
import { useMaybePubSub, useDebounce } from "../../hooks";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { nextFrame } from "../../utils";
import type { Timeout } from "../../types";
import { cardCollectionMotionVariants } from "../../config/cardCollectionMotion";
import type { ApplyMotionProp, ClassNameProp } from "../../props";

import {
  scrollContainerStyle,
  contentStyle,
  fillerStyle,
  carouselStyle,
  carouselWrapperStyle,
  innerScrollStyle,
  containerStyle,
  contentContainerStyle,
  containerMeasureStyle,
} from "./Carousel.css";
import { theme } from "../../theme/css/theme.css";
import { Container } from "../Container";

export type CarouselRef = HTMLDivElement & {
  toPrevious: () => number;
  toNext: () => number;
  setIndex: (index: number) => void;
};

export type CarouselProps = ApplyMotionProp &
  ClassNameProp & {
    align?: FlexProps["align"];
    titleTrailing?: ReactNode;
    leading?: ReactNode;
    trailing?: ReactNode;
    gap?: keyof typeof theme.spacings;
    contentClassName?: string;
    variant?: "centered" | "default";
    overlapColor?: ScrollPaneProps["overlapColor"];
    overlapSize?: ScrollPaneProps["overlapSize"];
    disabled?: boolean;
    filler?: boolean;
    children: ReactNode;
    fireIndexChange?: boolean;
    onIndexChange?: (index: number) => void;
    /**
     * Navigation component to render above the carousel
     */
    navigation?: ReactNode;
    /**
     * Optional CSS min-height value for the carousel wrapper.
     * Useful for ensuring a minimum viewport height, e.g. `calc(100dvh - 4rem)`.
     */
    minHeight?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<CarouselRef>;
  };

const SCROLL_END_DEBOUNCE_MS = 150;

/**
 * A carousel component with centered items and JS-based snapping.
 * Items are centered in the viewport, with filler elements ensuring
 * the first item starts centered.
 */
export const Carousel: FC<CarouselProps> = ({
  align = "flex-start",
  applyMotion = false,
  variant = "default",
  leading,
  gap = "s",
  trailing,
  contentClassName,
  overlapColor = "backgroundTint",
  overlapSize = "default",
  disabled = false,
  fireIndexChange = true,
  onIndexChange,
  filler = true,
  className,
  children,
  navigation,
  minHeight,
  ref,
}) => {
  const pubSub = useMaybePubSub();

  const internalRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const flexRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLElement[]>([]);
  const activeIndexRef = useRef(0);
  const scrollEndTimeoutRef = useRef<Timeout | null>(null);
  const isScrollingProgrammaticallyRef = useRef(false);
  const [isMounted, setIsMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const onIndexChangeRef = useRef(onIndexChange);
  useEffect(() => {
    onIndexChangeRef.current = onIndexChange;
  }, [onIndexChange]);

  const hasFiller = filler || variant === "centered";

  const [fillerWidth, setFillerWidth] = useState<number | null>(null);

  /**
   * Measures the filler width based on the scroll container and container probe widths.
   * Used for the "default" variant where the CSS-only approach (`100cqi`) does not
   * automatically update when the layout shifts (e.g. side content open/close animation).
   */
  const measureFiller = useCallback(() => {
    if (variant !== "default" || !hasFiller) return;
    if (!scrollRef.current || !containerRef.current) return;

    const contentWidth = scrollRef.current.clientWidth;
    const containerWidth = containerRef.current.offsetWidth;

    const SPACING_XS_PX = 8;
    setFillerWidth((contentWidth - containerWidth) / 2 + SPACING_XS_PX);
  }, [variant, hasFiller]);

  const debouncedMeasureFiller = useDebounce(measureFiller, 500);

  /**
   * Collect carousel items from flex container children
   */
  const collectItems = useCallback(() => {
    if (!flexRef.current) return;

    const items = Array.from(flexRef.current.children).filter(
      (child) => !child.hasAttribute("data-filler"),
    ) as HTMLElement[];

    itemsRef.current = items;
  }, []);

  /**
   * Find the closest item to the center of the viewport
   */
  const findClosestItemIndex = useCallback((): number => {
    if (!scrollRef.current || itemsRef.current.length === 0) return 0;

    const scrollCenter =
      scrollRef.current.scrollLeft + scrollRef.current.clientWidth / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    itemsRef.current.forEach((item, idx) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCenter - scrollCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });

    return closestIndex;
  }, []);

  /**
   * Update active index based on scroll position
   */
  const defineActiveIndex = useCallback(() => {
    if (!scrollRef.current) return;

    const closestIndex = findClosestItemIndex();

    if (closestIndex !== activeIndexRef.current) {
      activeIndexRef.current = closestIndex;
      setActiveIndex(closestIndex);
    }
  }, [findClosestItemIndex]);

  /**
   * Scroll to center an item in the viewport
   */
  const scrollToIndex = useCallback(
    (index: number, behavior: "smooth" | "instant" = "smooth") => {
      const item = itemsRef.current[index];
      if (!item || !scrollRef.current) return;

      isScrollingProgrammaticallyRef.current = true;

      // Calculate scroll position to center the item
      const containerCenter = scrollRef.current.clientWidth / 2;
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const targetScroll = itemCenter - containerCenter;

      // Clamp to valid scroll range
      const maxScroll =
        scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
      const clampedScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      scrollRef.current.scrollTo({
        left: clampedScroll,
        behavior,
      });

      // Update active index
      activeIndexRef.current = index;
      setActiveIndex(index);

      // Reset programmatic scroll flag after animation
      if (behavior === "smooth") {
        setTimeout(() => {
          isScrollingProgrammaticallyRef.current = false;
        }, 500);
      } else {
        isScrollingProgrammaticallyRef.current = false;
      }
    },
    [],
  );

  /**
   * Snap to the nearest item after scroll ends
   */
  const snapToNearestItem = useCallback(() => {
    if (isScrollingProgrammaticallyRef.current) return;

    const closestIndex = findClosestItemIndex();
    scrollToIndex(closestIndex, "smooth");
  }, [findClosestItemIndex, scrollToIndex]);

  const throttledDefineActiveIndex = useCallback(
    throttle(defineActiveIndex, 50),
    [defineActiveIndex],
  );

  /**
   * Handle scroll events - update active index and schedule snap.
   * For the "default" variant, CSS scroll snapping handles alignment;
   * JS snap scheduling is skipped.
   */
  const handleScroll = useCallback(() => {
    // Update active index during scroll
    throttledDefineActiveIndex();

    // CSS handles snapping for the default variant
    if (variant === "default") return;

    // Don't snap if we're scrolling programmatically
    if (isScrollingProgrammaticallyRef.current) return;

    // Clear pending snap
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
    }

    // Schedule snap after scroll ends
    scrollEndTimeoutRef.current = setTimeout(() => {
      snapToNearestItem();
    }, SCROLL_END_DEBOUNCE_MS);
  }, [variant, throttledDefineActiveIndex, snapToNearestItem]);

  const toPrevious = useCallback((): number => {
    if (itemsRef.current.length === 0) return 0;

    const newIndex =
      activeIndexRef.current - 1 < 0
        ? itemsRef.current.length - 1
        : activeIndexRef.current - 1;

    scrollToIndex(newIndex);
    return newIndex;
  }, [scrollToIndex]);

  const toNext = useCallback((): number => {
    if (itemsRef.current.length === 0) return 0;

    const newIndex =
      activeIndexRef.current + 1 >= itemsRef.current.length
        ? 0
        : activeIndexRef.current + 1;

    scrollToIndex(newIndex);
    return newIndex;
  }, [scrollToIndex]);

  const setIndex = useCallback(
    (index: number) => {
      scrollToIndex(index);
    },
    [scrollToIndex],
  );

  // Expose imperative methods
  useImperativeHandle(ref, () => {
    const element = internalRef.current;
    if (!element) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return null as any;
    }

    Object.assign(element, {
      toPrevious,
      toNext,
      setIndex,
    });

    return element as CarouselRef;
  }, [toPrevious, toNext, setIndex]);

  // Collect items on mount and when children change
  useIsomorphicLayoutEffect(() => {
    collectItems();
  }, [collectItems, children]);

  // Measure filler on mount and whenever children change layout — called directly
  // (not debounced) so the width is set synchronously before paint, preventing
  // an animated jump from the 0px fallback to the measured value.
  useIsomorphicLayoutEffect(() => {
    measureFiller();
  }, [measureFiller, children]);

  // Set ARIA attributes on items
  useEffect(() => {
    itemsRef.current.forEach((item, index) => {
      item.setAttribute(
        "aria-label",
        `Item ${index + 1} of ${itemsRef.current.length}`,
      );
      item.setAttribute("aria-describedby", `carousel-item-${index}`);
      item.setAttribute(
        "aria-selected",
        index === activeIndex ? "true" : "false",
      );
      item.setAttribute("role", "tabpanel");
      item.setAttribute("tabIndex", "-1");
    });
  }, [activeIndex, children]);

  // Handle initial mount and scroll to active item
  useEffect(() => {
    if (!internalRef.current || isMounted || itemsRef.current.length === 0)
      return;

    setIsMounted(true);

    const activeElement = internalRef.current.querySelector("[data-active]");
    if (activeElement) {
      const index = itemsRef.current.indexOf(activeElement as HTMLElement);
      if (index !== -1) {
        nextFrame(() => {
          scrollToIndex(index, "instant");
        }, 2);
      }
    }
  }, [isMounted, scrollToIndex, children]);

  // Fire onIndexChange callback
  useEffect(() => {
    if (!fireIndexChange) return;

    onIndexChangeRef.current?.(activeIndex);
  }, [activeIndex, fireIndexChange]);

  // Subscribe to pubSub scrollTo events
  useEffect(() => {
    if (!pubSub) return;

    const unsubscribe = pubSub.subscribe("scrollTo", ({ element }) => {
      if (
        element &&
        internalRef.current &&
        internalRef.current.contains(element)
      ) {
        const index = itemsRef.current.indexOf(element);
        if (index !== -1) {
          scrollToIndex(index);
        }
      }
    });

    return () => unsubscribe();
  }, [pubSub, scrollToIndex]);

  // Re-measure filler when layout changes (e.g. side content open/close animation ends)
  useEffect(() => {
    if (!pubSub || variant !== "default" || !hasFiller) return;

    const unsubscribe = pubSub.subscribe("recalculateLayout", () => {
      debouncedMeasureFiller();
    });

    return () => unsubscribe();
  }, [pubSub, variant, hasFiller, debouncedMeasureFiller]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
      }
    },
    [],
  );

  // Calculate gap value for CSS variable
  const gapValue = theme.spacings[gap] ?? theme.spacings.s;

  return (
    <Flex direction="column" overflowHidden>
      {variant === "default" && hasFiller && (
        <Container padding="none">
          <div ref={containerRef} className={containerMeasureStyle} />
        </Container>
      )}
      <Flex
        gap="s"
        direction="column"
        className={containerStyle}
        data-measured={
          variant === "default" && fillerWidth !== null ? "" : undefined
        }
        style={
          variant === "default" && fillerWidth !== null
            ? ({ "--filler-width": `${fillerWidth}px` } as CSSProperties & {
                "--filler-width": string;
              })
            : undefined
        }
      >
        {navigation}
        {children && (
          <motion.div
            ref={wrapperRef}
            className={mergeClasses(
              "carousel",
              carouselStyle,
              carouselWrapperStyle({ variant }),
              className,
            )}
            variants={applyMotion ? cardCollectionMotionVariants : undefined}
            initial={applyMotion ? "hidden" : undefined}
            animate={
              applyMotion && isMounted && itemsRef.current.length > 0
                ? "visible"
                : applyMotion
                  ? "hidden"
                  : undefined
            }
            exit={applyMotion ? "exit" : undefined}
            style={minHeight ? { minHeight } : undefined}
          >
            {/* biome-ignore lint/a11y/useSemanticElements: role="group" on div is the standard ARIA carousel pattern */}
            <div
              ref={internalRef}
              className={scrollContainerStyle}
              role="group"
              aria-label="carousel items"
              style={
                {
                  "--carousel-gap": gapValue,
                } as CSSProperties
              }
            >
              <ScrollPane
                ref={scrollRef}
                overlapColor={overlapColor}
                overlapSize={overlapSize}
                mode="horizontal"
                hideScrollbar
                tabIndex={-1}
                innerTabIndex={-1}
                onScroll={handleScroll}
                contentClassName={innerScrollStyle}
                disabled={disabled}
                emit
              >
                <Flex
                  ref={flexRef}
                  gap={gap}
                  direction="row"
                  align={align}
                  justify="flex-start"
                  className={mergeClasses(
                    contentContainerStyle,
                    contentStyle({ variant }),
                    contentClassName,
                  )}
                  data-variant={variant}
                  inline
                >
                  {leading}

                  {hasFiller && (
                    <div className={fillerStyle({ variant })} data-filler />
                  )}

                  <AnimatePresence>{children}</AnimatePresence>

                  {hasFiller && (
                    <div className={fillerStyle({ variant })} data-filler />
                  )}

                  {trailing}
                </Flex>
              </ScrollPane>
            </div>
          </motion.div>
        )}
      </Flex>
    </Flex>
  );
};
